import type { FC } from 'react'
import React, { memo, useMemo, useCallback, useState, useEffect } from 'react'
import { Box } from '@material-ui/core'
import { isEngageUser, useGetOverallRatingHistoryQuery } from '@homeviews/store'
import {
  Spinner,
  Select,
  LockedContentHeader,
  Typography,
  Tooltip,
} from '@homeviews/shared-ui'
import { mixpanelTrack, EVENTS } from '@homeviews/seo'
import NoData from '../NoData'
import { transformOverallRatingsHistoryData } from '../../helpers/transform'
import HistoryChart from '../SimpleHistoryChart'
import { isEqual } from '@homeviews/utils'
import ScreenshotButton from '../ScreenshotButton'
import {
  CHART_COLORS,
  DEVELOPMENT_SCORES_CATEGORY_OPTIONS,
} from '../../helpers/settings'
import { useSelector } from 'react-redux'

interface IProps {
  activeFilters: any
  graphDisabled?: boolean
  gridRef?: HTMLElement
  isStandardDeveloper: boolean
  filtersDetails: any
}

const OverallRatingHistoryChart: FC<IProps> = memo(
  ({
    activeFilters,
    graphDisabled,
    gridRef,
    isStandardDeveloper,
    filtersDetails,
  }) => {
    const [accessor, setAccessor] = useState<string>('rating')
    const isEngagePackage = Boolean(useSelector(isEngageUser))

    const { data: ratingHistoryData, isFetching } =
      useGetOverallRatingHistoryQuery(
        {
          ...activeFilters,
          engage: isEngagePackage,
        },
        {
          skip: graphDisabled,
        },
      )

    const data = useMemo(
      () =>
        transformOverallRatingsHistoryData(ratingHistoryData?.data, accessor),
      [ratingHistoryData?.data, accessor],
    )

    const handleSelectChange = (option: { value: string; label: 'string' }) => {
      setAccessor(option?.value || '')

      mixpanelTrack('DASHBOARD_CHART_FILTER_CHANGE', {
        'Chart Name': 'Ratings History',
        Filter: option?.label || '',
      })
    }

    if (isFetching) {
      return <Spinner centered />
    }

    if (!data || !data?.length) {
      if (gridRef) {
        gridRef.style.display = 'none'
        return <NoData />
      }
    } else {
      if (gridRef) {
        gridRef.style.display = 'block'
      }
    }

    return (
      <div>
        <Box justifyContent="space-between" display="flex" mb={2}>
          <LockedContentHeader isLocked={isStandardDeveloper}>
            <div className="h-exportableDataTitle">
              <Typography variant="h3" className="l-lockedContent__title">
                Ratings History
              </Typography>
              <ScreenshotButton />
              <Tooltip
                title="Ratings History"
                text="Discover how your overall scores have changed over time. Select the developments you wish to compare at the bottom of the graph and filter by the different categories."
              />
            </div>
          </LockedContentHeader>

          <div className="l-chart__selectFilterWrapper">
            <Select
              options={DEVELOPMENT_SCORES_CATEGORY_OPTIONS}
              value={accessor}
              onChange={handleSelectChange}
              alignRight
            />
          </div>
        </Box>
        {filtersDetails && filtersDetails}

        <HistoryChart
          data={data}
          additionalBottomText={
            ratingHistoryData?.truncated_developments
              ? 'Currently showing the top 10 developments by number of reviews. To view other developments, please use the development filter'
              : ''
          }
        />
      </div>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default OverallRatingHistoryChart
